import React, { useState } from 'react';
import { Box, List, ListItemButton, ListItemText, IconButton, Typography, Button, Stack, Tooltip, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, TextField } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Chat as ChatIcon, ChevronRight as ChevronRightIcon, ChevronLeft as ChevronLeftIcon, Edit as EditIcon, Check as CheckIcon, Close as CloseIcon } from '@mui/icons-material';
import { ConversationSummary, useUpdateConversationTitleMutation } from '../../../../../services/aiAgentApi';
import { useIntl } from 'react-intl';
import { getUserId } from '../../../../../utils/userUtils';

const COLLAPSED_WIDTH = 48;
const EXPANDED_WIDTH = 280;

interface ConversationSidebarProps {
	conversations: ConversationSummary[];
	selectedConversationId: string | undefined;
	onConversationSelect: (conversationId: string) => void;
	onNewConversation: () => void;
	onDeleteConversation: (conversationId: string) => void;
	loading?: boolean;
	isCollapsed?: boolean;
	onToggleCollapse?: () => void;
}

export const ConversationSidebar: React.FC<ConversationSidebarProps> = ({
	conversations,
	selectedConversationId,
	onConversationSelect,
	onNewConversation,
	onDeleteConversation,
	loading = false,
	isCollapsed = false,
	onToggleCollapse,
}) => {
	const { formatMessage } = useIntl();
	const userId = getUserId();
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [conversationToDelete, setConversationToDelete] = useState<string | null>(null);
	const [editingConversationId, setEditingConversationId] = useState<string | null>(null);
	const [editingTitle, setEditingTitle] = useState('');
	const [updateConversationTitle, { isLoading: isUpdatingTitle }] = useUpdateConversationTitleMutation();

	const handleDeleteClick = (e: React.MouseEvent, conversationId: string) => {
		e.stopPropagation();
		setConversationToDelete(conversationId);
		setDeleteDialogOpen(true);
	};

	const handleDeleteConfirm = () => {
		if (conversationToDelete) {
			onDeleteConversation(conversationToDelete);
		}
		setDeleteDialogOpen(false);
		setConversationToDelete(null);
	};

	const handleDeleteCancel = () => {
		setDeleteDialogOpen(false);
		setConversationToDelete(null);
	};

	const handleEditClick = (e: React.MouseEvent, conversationId: string, currentTitle: string) => {
		e.stopPropagation();
		setEditingConversationId(conversationId);
		setEditingTitle(currentTitle || '');
	};

	const handleEditCancel = (e?: React.MouseEvent) => {
		e?.stopPropagation();
		setEditingConversationId(null);
		setEditingTitle('');
	};

	const handleEditSave = async (e?: React.MouseEvent) => {
		e?.stopPropagation();
		if (editingConversationId && editingTitle.trim()) {
			try {
				await updateConversationTitle({
					userId: userId,
					conversationId: editingConversationId,
					title: editingTitle.trim(),
				}).unwrap();
			} catch (error) {
				console.error('Error updating conversation title:', error);
			}
		}
		setEditingConversationId(null);
		setEditingTitle('');
	};

	const handleEditKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			handleEditSave();
		} else if (e.key === 'Escape') {
			handleEditCancel();
		}
	};

	// Sort conversations by updatedAt (most recent first)
	const sortedConversations = [...conversations].sort(
		(a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
	);

	// Collapsed view - just icons, click to expand
	if (isCollapsed) {
		return (
			<Box
				sx={{
					width: COLLAPSED_WIDTH,
					height: '100%',
					borderRight: '1px solid',
					borderColor: 'divider',
					display: 'flex',
					flexDirection: 'column',
					bgcolor: 'grey.900',
					alignItems: 'center',
					py: 1,
					gap: 0.5,
				}}
			>
				{/* Expand button */}
				<Tooltip title={formatMessage({ id: 'chat.conversations' }, { defaultMessage: 'Conversations' })} placement='right'>
					<IconButton
						size='small'
						onClick={onToggleCollapse}
						sx={{ color: 'grey.400', '&:hover': { bgcolor: 'action.hover', color: 'primary.main' } }}
					>
						<ChevronRightIcon fontSize='small' />
					</IconButton>
				</Tooltip>

				<Box sx={{ borderBottom: '1px solid', borderColor: 'grey.700', width: '70%', my: 0.5 }} />

				{/* New chat icon */}
				<Tooltip title={formatMessage({ id: 'chat.newChat' }, { defaultMessage: 'New Chat' })} placement='right'>
					<IconButton
						size='small'
						onClick={onNewConversation}
						disabled={loading}
						sx={{ color: 'grey.400', '&:hover': { bgcolor: 'action.hover', color: 'primary.main' } }}
					>
						<AddIcon fontSize='small' />
					</IconButton>
				</Tooltip>

				<Box sx={{ borderBottom: '1px solid', borderColor: 'grey.700', width: '70%', my: 0.5 }} />

				{/* Conversation icons - scrollable list */}
				<Box sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
					{sortedConversations.map((conversation, index) => (
						<Tooltip 
							key={conversation.conversationId} 
							title={conversation.title || `Conversation ${index + 1}`} 
							placement='right'
						>
							<IconButton
								size='small'
								onClick={() => onConversationSelect(conversation.conversationId)}
								sx={{
									color: selectedConversationId === conversation.conversationId ? 'primary.main' : 'grey.500',
									bgcolor: selectedConversationId === conversation.conversationId ? 'action.selected' : 'transparent',
									'&:hover': { bgcolor: 'action.hover' },
									fontSize: '0.75rem',
									width: 32,
									height: 32,
								}}
							>
								<ChatIcon fontSize='small' />
							</IconButton>
						</Tooltip>
					))}
				</Box>
			</Box>
		);
	}

	// Expanded view
	return (
		<Box
			sx={{
				width: EXPANDED_WIDTH,
				height: '100%',
				borderRight: '1px solid',
				borderColor: 'divider',
				display: 'flex',
				flexDirection: 'column',
				bgcolor: 'background.paper',
			}}
		>
			{/* Header with title and collapse button */}
			<Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
				<Typography variant='subtitle1' sx={{ fontWeight: 700 }}>
					{formatMessage({ id: 'chat.financingAgent' }, { defaultMessage: 'Rahoitusagentti' })}
				</Typography>
				<Tooltip title={formatMessage({ id: 'chat.collapse' }, { defaultMessage: 'Collapse' })} placement='right'>
					<IconButton size='small' onClick={onToggleCollapse}>
						<ChevronLeftIcon fontSize='small' />
					</IconButton>
				</Tooltip>
			</Box>

			{/* New Chat button */}
			<Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
				<Button
					fullWidth
					variant='text'
					startIcon={<AddIcon />}
					onClick={onNewConversation}
					disabled={loading}
					size='small'
					sx={{
						justifyContent: 'flex-start',
						color: 'text.secondary',
						textTransform: 'none',
						'&:hover': {
							bgcolor: 'action.hover',
							color: 'primary.main',
						},
					}}
				>
					{formatMessage({ id: 'chat.newChat' }, { defaultMessage: 'New Chat' })}
				</Button>
			</Box>

			{/* Conversations list */}
			<List sx={{ flex: 1, overflow: 'auto', p: 0 }}>
				{sortedConversations.length === 0 ? (
					<Box sx={{ p: 2, textAlign: 'center' }}>
						<Typography variant='body2' color='text.secondary'>
							{formatMessage({ id: 'chat.noConversations' }, { defaultMessage: 'No conversations yet' })}
						</Typography>
					</Box>
				) : (
					sortedConversations.map(conversation => (
						<ListItemButton
							key={conversation.conversationId}
							selected={selectedConversationId === conversation.conversationId}
							onClick={() => editingConversationId !== conversation.conversationId && onConversationSelect(conversation.conversationId)}
							sx={{
								borderBottom: '1px solid',
								borderColor: 'divider',
								'&.Mui-selected': {
									bgcolor: 'action.selected',
									'&:hover': {
										bgcolor: 'action.selected',
									},
								},
								'&:hover': {
									bgcolor: 'action.hover',
								},
							}}
						>
							{editingConversationId === conversation.conversationId ? (
								<Box sx={{ display: 'flex', alignItems: 'center', flex: 1, gap: 0.5 }} onClick={e => e.stopPropagation()}>
									<TextField
										value={editingTitle}
										onChange={e => setEditingTitle(e.target.value)}
										onKeyDown={handleEditKeyDown}
										size='small'
										autoFocus
										fullWidth
										disabled={isUpdatingTitle}
										sx={{
											'& .MuiInputBase-input': {
												py: 0.5,
												px: 1,
												fontSize: '0.875rem',
											},
										}}
									/>
									<IconButton size='small' onClick={handleEditSave} disabled={isUpdatingTitle}>
										<CheckIcon fontSize='small' color='success' />
									</IconButton>
									<IconButton size='small' onClick={handleEditCancel} disabled={isUpdatingTitle}>
										<CloseIcon fontSize='small' color='error' />
									</IconButton>
								</Box>
							) : (
								<>
									<ListItemText
										primary={
											<Typography variant='body2' noWrap>
												{conversation.title || `Conversation - ${new Date(conversation.updatedAt).toLocaleDateString()}`}
											</Typography>
										}
										secondary={
											<Stack direction='row' spacing={1} alignItems='center'>
												<Typography variant='caption' color='text.secondary' noWrap>
													{new Date(conversation.updatedAt).toLocaleString()}
												</Typography>
												{conversation.messageCount > 0 && (
													<Typography variant='caption' color='text.secondary'>
														({conversation.messageCount})
													</Typography>
												)}
											</Stack>
										}
									/>
									<Tooltip title={formatMessage({ id: 'chat.editTitle' }, { defaultMessage: 'Edit title' })}>
										<IconButton
											size='small'
											aria-label='edit'
											onClick={e => handleEditClick(e, conversation.conversationId, conversation.title || '')}
											sx={{ ml: 0.5 }}
										>
											<EditIcon fontSize='small' />
										</IconButton>
									</Tooltip>
									<Tooltip title={formatMessage({ id: 'common.delete' }, { defaultMessage: 'Delete' })}>
										<IconButton
											size='small'
											edge='end'
											aria-label='delete'
											onClick={e => handleDeleteClick(e, conversation.conversationId)}
											sx={{ ml: 0.5 }}
										>
											<DeleteIcon fontSize='small' />
										</IconButton>
									</Tooltip>
								</>
							)}
						</ListItemButton>
					))
				)}
			</List>

			{/* Delete confirmation dialog */}
			<Dialog
				open={deleteDialogOpen}
				onClose={handleDeleteCancel}
				aria-labelledby='delete-dialog-title'
				aria-describedby='delete-dialog-description'
			>
				<DialogTitle id='delete-dialog-title'>
					{formatMessage({ id: 'chat.deleteConversation.title' }, { defaultMessage: 'Delete conversation?' })}
				</DialogTitle>
				<DialogContent>
					<DialogContentText id='delete-dialog-description'>
						{formatMessage({ id: 'chat.deleteConversation.message' }, { defaultMessage: 'Are you sure you want to delete this conversation? This action cannot be undone.' })}
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleDeleteCancel}>
						{formatMessage({ id: 'common.cancel' }, { defaultMessage: 'Cancel' })}
					</Button>
					<Button onClick={handleDeleteConfirm} color='error' variant='contained'>
						{formatMessage({ id: 'common.delete' }, { defaultMessage: 'Delete' })}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};
